'use client';

import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import {
  FileXls,
  FileCsv,
  Upload,
  CheckCircle,
  Spinner,
  X,
  Warning,
} from '@phosphor-icons/react';
import { useUploadedFileStore } from '../../../stores/uploadedFileStore';
import { useSchema } from '../../../contexts/SchemaContext';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { unstable_batchedUpdates } from 'react-dom';
import type { Column, DataType } from '../../../types';

interface FileUploadWidgetProps {
  nodeId: string;
  /** 'excel' 限制只接收 .xlsx/.xls; 'csv' 只接收 .csv; 'both' 两者均接 */
  accept: 'excel' | 'csv' | 'both';
  /** 上传成功后回调（用于更新节点 config 里的 fileName 参数） */
  onUploaded?: (fileName: string, rowCount: number, columns: string[]) => void;
}

function inferDataType(values: unknown[]): DataType {
  const nonNull = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (nonNull.length === 0) return 'string';
  if (nonNull.every((v) => typeof v === 'number' || !isNaN(Number(v)))) return 'number';
  if (nonNull.every((v) => /^\d{4}[-/]\d{2}[-/]\d{2}/.test(String(v)))) return 'date';
  return 'string';
}

/**
 * Normalize a raw cell value from xlsx into a displayable string / primitive.
 *
 * xlsx returns:
 *  - Date objects when cellDates:true is used and the cell has a date format
 *  - numbers for numeric cells
 *  - strings for text cells
 *  - booleans for boolean cells
 *
 * Dates are formatted as "YYYY-MM-DD" (date-only) or "YYYY-MM-DD HH:mm:ss"
 * (when the time component is non-zero), matching the Go server output.
 */
function normalizeCellValue(val: unknown): unknown {
  if (val instanceof Date) {
    const y = val.getFullYear();
    const mo = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    const h = val.getHours();
    const mi = val.getMinutes();
    const s = val.getSeconds();
    if (h === 0 && mi === 0 && s === 0) {
      return `${y}-${mo}-${d}`;
    }
    return `${y}-${mo}-${d} ${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return val;
}

const ACCEPT_MAP = {
  excel: '.xlsx,.xls',
  csv: '.csv',
  both: '.xlsx,.xls,.csv',
};

const ACCEPT_REGEX: Record<FileUploadWidgetProps['accept'], RegExp> = {
  excel: /\.(xlsx|xls)$/i,
  csv: /\.csv$/i,
  both: /\.(xlsx|xls|csv)$/i,
};

export function FileUploadWidget({ nodeId, accept, onUploaded }: FileUploadWidgetProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { files, setFileForNode, clearFileForNode, isUploading, setIsUploading } = useUploadedFileStore();
  const { loadSchemaFromDataFile } = useSchema();
  const { updateNodeConfig } = useWorkflowStore();

  // Each node manages its own file independently
  const uploadedFile = files[nodeId] ?? null;

  const processFile = async (file: File) => {
    setError(null);
    if (!ACCEPT_REGEX[accept].test(file.name)) {
      setError(`不支持此文件格式，请上传 ${ACCEPT_MAP[accept]} 文件`);
      return;
    }

    setIsUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      // cellDates:true makes xlsx return JS Date objects for date-formatted cells
      // instead of raw Excel serial numbers (e.g. 45678).
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<unknown[]>(firstSheet, { header: 1 });

      if (jsonData.length < 2) {
        setError('文件内容不足，至少需要一行表头和一行数据');
        return;
      }

      const headers = (jsonData[0] as unknown[]).map((h) => String(h).trim());
      const dataRows = jsonData.slice(1).map((row) => {
        const obj: Record<string, unknown> = {};
        headers.forEach((h, i) => {
          obj[h] = normalizeCellValue((row as unknown[])[i] ?? null);
        });
        return obj;
      });

      const columns: Column[] = headers.map((name, idx) => ({
        name,
        type: inferDataType(dataRows.map((r) => r[name])),
        nullable: dataRows.some((r) => r[name] === null || r[name] === undefined || r[name] === ''),
        index: idx,
      }));

      const fileId = `file_${nodeId}`;
      // Batch all state updates together to avoid intermediate re-renders
      unstable_batchedUpdates(() => {
        setFileForNode(nodeId, { id: fileId, name: file.name, rowCount: dataRows.length, columns: headers, data: dataRows });
        loadSchemaFromDataFile(fileId, file.name, columns);
        const nodeType = accept === 'csv' ? 'load_csv' : 'load_excel';
        updateNodeConfig(nodeId, { type: nodeType, params: { fileName: file.name, rowCount: dataRows.length } });
      });
      onUploaded?.(file.name, dataRows.length, headers);
    } catch {
      setError('文件解析失败，请检查文件格式');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleClear = () => {
    unstable_batchedUpdates(() => {
      clearFileForNode(nodeId);
      updateNodeConfig(nodeId, {
        type: accept === 'csv' ? 'load_csv' : 'load_excel',
        params: {},
      });
    });
  };

  const isThisNodeFile = !!uploadedFile;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-zinc-500">数据文件</span>
        {isThisNodeFile && (
          <button
            onClick={handleClear}
            className="text-[10px] text-red-400 hover:text-red-600 transition-colors"
          >
            清除
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isThisNodeFile && uploadedFile ? (
          /* ── Uploaded state ── */
          <motion.div
            key="uploaded"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100"
          >
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              {accept === 'csv'
                ? <FileCsv className="w-5 h-5 text-emerald-600" weight="fill" />
                : <FileXls className="w-5 h-5 text-emerald-600" weight="fill" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-emerald-900 truncate">{uploadedFile.name}</p>
              <p className="text-[10px] text-emerald-600">
                {uploadedFile.rowCount.toLocaleString()} 行 · {uploadedFile.columns.length} 列
              </p>
            </div>
            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" weight="fill" />
          </motion.div>
        ) : (
          /* ── Upload zone ── */
          <motion.div
            key="upload-zone"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
          >
            <div
              className={[
                'relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-200',
                isDragging
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50/30',
              ].join(' ')}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-2">
                {isUploading ? (
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                    <Spinner className="w-7 h-7 text-emerald-400" weight="bold" />
                  </motion.div>
                ) : (
                  <Upload className="w-7 h-7 text-zinc-300" />
                )}
                <div>
                  <p className="text-xs font-medium text-zinc-600">
                    {isUploading ? '解析中…' : '拖拽或点击上传'}
                  </p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">支持 {ACCEPT_MAP[accept]}</p>
                </div>
              </div>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1.5 mt-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg overflow-hidden"
                >
                  <Warning className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  <p className="text-[11px] text-red-600 flex-1">{error}</p>
                  <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_MAP[accept]}
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  );
}
